import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { LEGAL_PAGES, LegalPage, LegalPageKey } from './legal-content';
import { LegalFooterComponent } from './legal-footer.component';
import { AuthService } from '../service/auth/auth.service';
import { isCapacitorNative } from '../service/platform/capacitor-platform';
import { BrandService } from '../service/brand/brand.service';

@Component({
  selector: 'app-legal-page',
  standalone: true,
  imports: [CommonModule, RouterLink, LegalFooterComponent],
  template: `
    <div class="legal-shell">
      <header class="legal-header">
        <a routerLink="/" class="legal-brand">{{ brand.productName }}</a>
        <a *ngIf="!isSignedIn" routerLink="/login">Sign in</a>
        <a *ngIf="isSignedIn && !isNative" routerLink="/dashboard">Dashboard</a>
      </header>
      <main class="legal-card">
        <h1>{{ page.title }}</h1>
        <p class="legal-updated">Last updated: {{ page.updated }}</p>
        <aside *ngIf="page.counselNotice" class="legal-review" role="note">
          <strong>Counsel review required:</strong> {{ page.counselNotice }}
        </aside>
        <section *ngFor="let section of page.sections">
          <h2>{{ section.heading }}</h2>
          <p *ngFor="let paragraph of section.paragraphs">{{ paragraph }}</p>
        </section>
        <div class="legal-actions" *ngIf="pageKey === 'account-deletion'">
          <a *ngIf="isSignedIn" class="btn btn-danger" routerLink="/dashboard/my-profile">Open My Profile</a>
          <a *ngIf="!isSignedIn" class="btn btn-primary" routerLink="/login">Sign in</a>
        </div>
      </main>
      <app-legal-footer></app-legal-footer>
    </div>
  `,
  styles: [`
    .legal-shell { min-height: 100vh; background: #f6f8fb; padding: 24px; color: #152033; }
    .legal-header, .legal-card, app-legal-footer { display: block; max-width: 880px; margin-inline: auto; }
    .legal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
    .legal-brand { color: inherit; font-size: 1.25rem; font-weight: 700; text-decoration: none; }
    .legal-card { background: white; border: 1px solid #dde6f1; border-radius: 16px; padding: clamp(24px, 5vw, 56px); }
    h1 { margin: 0; font-size: clamp(2rem, 5vw, 3rem); }
    h2 { margin: 32px 0 10px; font-size: 1.25rem; }
    p { line-height: 1.65; }
    .legal-updated { color: #617086; }
    .legal-review { margin: 24px 0; padding: 16px; border-left: 4px solid #ef8b00; background: #fff7e8; }
    .legal-actions { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 32px; }
  `],
})
export class LegalPageComponent {
  readonly pageKey: LegalPageKey;
  readonly page: LegalPage;
  readonly isNative = isCapacitorNative();
  protected readonly brand = inject(BrandService);

  constructor(route: ActivatedRoute, private auth: AuthService) {
    this.pageKey = route.snapshot.data['legalPage'] as LegalPageKey;
    this.page = LEGAL_PAGES[this.pageKey];
  }

  get isSignedIn(): boolean {
    return this.auth.isAuthenticated();
  }
}
